import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search, BookOpen, ExternalLink, ListChecks, ArrowLeft,
  Lightbulb, Link2, Rocket, Key, Settings, ChevronRight,
} from 'lucide-react';
import {
  TUTORIAL_CATEGORIES,
  TUTORIAL_SECTIONS,
  QUICK_WORKFLOWS,
  API_SETUP_BLOCKS,
  API_MODULE_MATRIX,
  SETTINGS_API_KEYS_PATH,
  searchTutorial,
  searchWorkflows,
  getSectionById,
  getSectionsByCategory,
  GUIDE_STORAGE_KEY,
} from '../data/platformTutorial';
import ModuleDemoVideo from '../components/ModuleDemoVideo';
import { getModuleDemoVideo } from '../data/moduleDemoVideos';

function ApiKnowledgeBase() {
  const navigate = useNavigate();

  return (
    <div className="mt-8 space-y-6 border-t border-white/10 pt-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <Key size={16} className="text-sky-400" />
          API knowledge base
        </h3>
        <button
          type="button"
          onClick={() => navigate(SETTINGS_API_KEYS_PATH)}
          className="inline-flex items-center gap-2 rounded-lg border border-sky-500/40 bg-sky-500/10 px-3 py-1.5 text-xs font-medium text-sky-200 hover:bg-sky-500/20"
        >
          <Settings size={14} />
          Open Settings → API Keys
        </button>
      </div>

      <p className="text-xs text-zinc-500 leading-relaxed">
        All keys are configured in Settings → API Keys and apply across every module on your account.
        Use Test connection after saving. OAuth-based apps may also require Connections.
      </p>

      {/* Module → keys matrix */}
      <div className="rounded-xl border border-white/10 overflow-hidden">
        <div className="bg-white/[0.03] px-4 py-2 border-b border-white/10">
          <p className="text-xs font-semibold text-zinc-300">Which module needs which keys?</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-white/5 text-zinc-500">
                <th className="px-4 py-2 font-medium">Module</th>
                <th className="px-4 py-2 font-medium">API categories</th>
                <th className="px-4 py-2 font-medium">Minimum keys</th>
              </tr>
            </thead>
            <tbody>
              {API_MODULE_MATRIX.map((row) => (
                <tr key={row.module} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-4 py-2.5 text-zinc-200">{row.module}</td>
                  <td className="px-4 py-2.5 text-zinc-500 capitalize">{row.categories.join(', ')}</td>
                  <td className="px-4 py-2.5 text-emerald-400/90 font-mono text-[11px]">{row.minKeys}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Per-category blocks */}
      <div className="space-y-4">
        {API_SETUP_BLOCKS.map((block) => (
          <div key={block.id} className="rounded-xl border border-white/10 overflow-hidden">
            <div className={`px-4 py-3 bg-gradient-to-r ${block.color} bg-opacity-20`}>
              <p className="font-semibold text-sm text-white">{block.label}</p>
              <p className="text-[11px] text-white/70 mt-0.5">
                Used by: {block.modules.join(' · ')}
              </p>
            </div>
            <div className="p-4 space-y-4">
              <ol className="space-y-1.5">
                {block.setup.map((step, i) => (
                  <li key={i} className="flex gap-2 text-xs text-zinc-400">
                    <span className="text-violet-400 shrink-0">{i + 1}.</span>
                    {step}
                  </li>
                ))}
              </ol>
              <div className="space-y-2">
                {block.keys.map((k) => (
                  <div key={k.key} className="rounded-lg bg-black/30 border border-white/5 px-3 py-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <code className="text-[11px] text-violet-300">{k.key}</code>
                      {k.required && (
                        <span className="text-[10px] rounded-full bg-amber-500/20 text-amber-300 px-1.5 py-0.5">Required</span>
                      )}
                    </div>
                    <p className="text-xs text-zinc-500 mt-1">{k.usedFor}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SectionDetail({ selected, onSelectRelated, navigate }) {
  if (!selected) return null;

  return (
    <>
      <h2 className="text-xl sm:text-2xl font-bold text-white">{selected.title}</h2>
      <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{selected.summary}</p>

      {getModuleDemoVideo(selected.id) && (
        <div className="mt-4">
          <ModuleDemoVideo moduleKey={selected.id} />
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {selected.path && (
          <button
            type="button"
            onClick={() => navigate(selected.path)}
            className="inline-flex items-center gap-2 rounded-lg bg-violet-500 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-400"
          >
            <ExternalLink size={14} />
            {selected.pathLabel || 'Open module'}
          </button>
        )}
        {selected.id !== 'api-setup' && (
          <button
            type="button"
            onClick={() => onSelectRelated('api-setup')}
            className="inline-flex items-center gap-2 rounded-lg border border-sky-500/30 bg-sky-500/10 px-4 py-2 text-sm text-sky-200 hover:bg-sky-500/20"
          >
            <Key size={14} />
            API setup guide
          </button>
        )}
      </div>

      <div className="mt-6 space-y-4">
        <h3 className="text-xs uppercase tracking-wider text-zinc-500">How to use</h3>
        {selected.steps.map((step, i) => (
          <div key={i} className="flex gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/10 text-xs font-bold text-zinc-300">
              {i + 1}
            </span>
            <div>
              <p className="text-sm font-medium text-white">{step.title}</p>
              <p className="text-xs text-zinc-500 mt-0.5 leading-relaxed">{step.body}</p>
            </div>
          </div>
        ))}
      </div>

      {selected.showApiKnowledgeBase && <ApiKnowledgeBase />}

      {selected.tips?.length > 0 && (
        <div className="mt-6 rounded-lg border border-amber-500/20 bg-amber-500/5 p-4">
          <p className="text-xs font-semibold text-amber-200 flex items-center gap-1.5 mb-2">
            <Lightbulb size={14} /> Pro tips
          </p>
          <ul className="space-y-1.5">
            {selected.tips.map((tip, i) => (
              <li key={i} className="text-xs text-amber-100/80">• {tip}</li>
            ))}
          </ul>
        </div>
      )}

      {selected.related?.length > 0 && (
        <div className="mt-6">
          <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1">
            <Link2 size={12} /> Related
          </p>
          <div className="flex flex-wrap gap-2">
            {selected.related.map((rid) => {
              const rel = getSectionById(rid);
              if (!rel) return null;
              return (
                <button
                  key={rid}
                  type="button"
                  onClick={() => onSelectRelated(rid)}
                  className="rounded-lg border border-white/10 px-2.5 py-1 text-xs text-zinc-400 hover:text-white hover:border-white/20"
                >
                  {rel.title}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}

export default function PlatformGuide() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('start');
  const sectionParam = searchParams.get('section');

  const filteredSections = useMemo(() => {
    if (query.trim()) return searchTutorial(query);
    return getSectionsByCategory(activeCategory);
  }, [query, activeCategory]);

  const filteredWorkflows = useMemo(() => searchWorkflows(query), [query]);

  const [selectedId, setSelectedId] = useState(sectionParam || 'welcome');
  const selected = getSectionById(selectedId) || TUTORIAL_SECTIONS[0];

  useEffect(() => {
    if (sectionParam && getSectionById(sectionParam)) {
      setSelectedId(sectionParam);
      const sec = getSectionById(sectionParam);
      if (sec?.category) setActiveCategory(sec.category);
    }
  }, [sectionParam]);

  const selectSection = (id) => {
    setSelectedId(id);
    setSearchParams({ section: id });
    const sec = getSectionById(id);
    if (sec?.category) setActiveCategory(sec.category);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#080808] text-white flex flex-col" data-testid="platform-guide-fullscreen">
      {/* Fullscreen header */}
      <header className="sticky top-0 z-50 border-b border-white/[0.06] bg-[#080808]/95 backdrop-blur-xl">
        <div className="flex items-center gap-3 px-4 sm:px-6 py-3">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-zinc-300 hover:bg-white/[0.08] hover:text-white transition-colors"
            data-testid="guide-back-dashboard"
          >
            <ArrowLeft size={16} />
            <span className="hidden sm:inline">Back to Dashboard</span>
            <span className="sm:hidden">Dashboard</span>
          </button>
          <div className="h-5 w-px bg-white/10" />
          <BookOpen size={18} className="text-indigo-400 shrink-0" />
          <div className="min-w-0 flex-1">
            <h1 className="text-sm font-semibold truncate">Platform Guide</h1>
            <p className="text-[11px] text-zinc-500 truncate hidden sm:block">Modules, workflows & API setup</p>
          </div>
          <button
            type="button"
            onClick={() => navigate(SETTINGS_API_KEYS_PATH)}
            className="hidden md:inline-flex items-center gap-2 rounded-lg border border-sky-500/30 bg-sky-500/10 px-3 py-2 text-xs text-sky-200 hover:bg-sky-500/20"
          >
            <Key size={14} />
            API Keys
          </button>
        </div>
      </header>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left nav — desktop */}
        <aside className="hidden lg:flex w-72 shrink-0 flex-col border-r border-white/[0.06] bg-[#0a0a0a]">
          <div className="p-4 border-b border-white/[0.06]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search guide…"
                className="w-full rounded-lg border border-white/10 bg-black/40 py-2 pl-9 pr-3 text-xs text-white placeholder:text-zinc-600 focus:outline-none focus:border-violet-500/40"
              />
            </div>
          </div>
          <div className="px-3 py-2 flex flex-wrap gap-1 border-b border-white/[0.06]">
            {TUTORIAL_CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => { setActiveCategory(cat.id); setQuery(''); }}
                className={`rounded-md px-2 py-1 text-[10px] font-medium transition-colors ${
                  activeCategory === cat.id && !query
                    ? 'bg-violet-500/20 text-violet-200'
                    : 'text-zinc-600 hover:text-zinc-400'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
          <ul className="flex-1 overflow-y-auto py-2">
            {filteredSections.map((sec) => (
              <li key={sec.id}>
                <button
                  type="button"
                  onClick={() => selectSection(sec.id)}
                  className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                    selectedId === sec.id
                      ? 'bg-violet-500/10 text-white border-r-2 border-violet-400'
                      : 'text-zinc-500 hover:text-zinc-200 hover:bg-white/[0.03]'
                  }`}
                >
                  <span className="line-clamp-1">{sec.title}</span>
                </button>
              </li>
            ))}
          </ul>
          <div className="p-3 border-t border-white/[0.06]">
            <button
              type="button"
              onClick={() => selectSection('api-setup')}
              className="w-full flex items-center justify-between rounded-lg bg-sky-500/10 border border-sky-500/20 px-3 py-2 text-xs text-sky-200 hover:bg-sky-500/20"
            >
              <span className="flex items-center gap-2"><Key size={14} /> API setup</span>
              <ChevronRight size={14} />
            </button>
          </div>
        </aside>

        {/* Main scroll area */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
            {/* Mobile search + categories */}
            <div className="lg:hidden mb-6 space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search guide…"
                  className="w-full rounded-xl border border-white/10 bg-black/40 py-2.5 pl-9 pr-3 text-sm text-white"
                />
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {TUTORIAL_CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => { setActiveCategory(cat.id); setQuery(''); }}
                    className={`shrink-0 rounded-full px-3 py-1 text-xs ${
                      activeCategory === cat.id && !query
                        ? 'bg-violet-500/20 text-violet-200 border border-violet-500/40'
                        : 'border border-white/10 text-zinc-500'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
              {!query && (
                <select
                  value={selectedId}
                  onChange={(e) => selectSection(e.target.value)}
                  className="w-full rounded-lg border border-white/10 bg-[#0d0d14] px-3 py-2 text-sm text-white"
                >
                  {filteredSections.map((sec) => (
                    <option key={sec.id} value={sec.id}>{sec.title}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Hero — only on welcome when not searching */}
            {!query && selectedId === 'welcome' && (
              <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-violet-500/10 to-transparent p-6 mb-8">
                <h2 className="text-2xl font-bold">How can we help?</h2>
                <p className="mt-2 text-sm text-zinc-400">
                  Browse modules, follow workflows, and configure APIs for the entire platform.
                </p>
                <button
                  type="button"
                  onClick={() => { localStorage.removeItem(GUIDE_STORAGE_KEY); window.location.reload(); }}
                  className="mt-2 text-xs text-violet-400 hover:underline"
                >
                  Replay welcome tour
                </button>
              </div>
            )}

            {/* Workflows */}
            {!query && selectedId === 'welcome' && (
              <section className="mb-8">
                <h2 className="text-sm font-semibold text-zinc-300 flex items-center gap-2 mb-4">
                  <ListChecks size={16} className="text-emerald-400" /> Popular workflows
                </h2>
                <div className="grid gap-3 sm:grid-cols-2">
                  {QUICK_WORKFLOWS.slice(0, 6).map((wf) => (
                    <button
                      key={wf.id}
                      type="button"
                      onClick={() => navigate(wf.path)}
                      className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-left hover:border-violet-500/30 transition-colors"
                    >
                      <p className="font-medium text-white text-sm">{wf.title}</p>
                      <p className="text-[10px] text-zinc-500 mt-1">{wf.duration}</p>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {query && filteredWorkflows.length > 0 && (
              <section className="mb-6 flex flex-wrap gap-2">
                {filteredWorkflows.map((wf) => (
                  <button
                    key={wf.id}
                    type="button"
                    onClick={() => navigate(wf.path)}
                    className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-xs text-emerald-200"
                  >
                    {wf.title} →
                  </button>
                ))}
              </section>
            )}

            {/* Article */}
            <article className="rounded-xl border border-white/10 bg-[#0d0d14] p-5 sm:p-8">
              <SectionDetail
                selected={selected}
                onSelectRelated={selectSection}
                navigate={navigate}
              />
            </article>

            <div className="mt-8 rounded-xl border border-white/5 bg-white/[0.02] p-6 text-center">
              <Rocket className="mx-auto text-violet-400 mb-2" size={24} />
              <p className="text-sm text-zinc-400">
                Still stuck?{' '}
                <button type="button" onClick={() => navigate('/support')} className="text-violet-300 underline">Support</button>
                {' · '}
                <button type="button" onClick={() => navigate('/dashboard')} className="text-violet-300 underline">Dashboard</button>
              </p>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
